import React, { useRef, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowUpRight,
  ArrowDown,
  Megaphone,
  Camera,
  Monitor,
  Compass,
  Check,
  Target,
  Rocket,
  TrendingUp,
  Quote,
  Sparkles,
  Clapperboard,
  ScanEye,
} from "lucide-react";
import { useLanguage } from "../context/LanguageContext";
import { ROUTES, servicePath } from "@/lib/routes";
import LandscapeCta from "../components/LandscapeCta";
import { useCookieConsent } from "@/context/CookieConsentContext";
import { Marquee } from "../components/ui/3d-testimonials";
import { ScrollStrip, useScrollStripMeasure } from "../components/ui/scroll-strip";
import { Avatar, AvatarFallback } from "../components/ui/avatar";

function youtubeEmbedUrl(url) {
  if (!url || typeof url !== "string") return null;
  const m = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}?autoplay=1&mute=1&loop=1&playlist=${m[1]}&controls=0&playsinline=1` : null;
}

function PortraitMediaCard({ item }) {
  const measureOnly = useScrollStripMeasure();
  const videoRef = React.useRef(null);
  const { hasConsent } = useCookieConsent();
  const marketingOk = hasConsent("marketing");
  const youtubeUrl = youtubeEmbedUrl(item.mediaUrl);
  const embed = marketingOk ? youtubeUrl : null;
  const blockedThirdParty = Boolean(youtubeUrl && !marketingOk);
  const isVideo = item.mediaType === "video" && item.mediaUrl && !youtubeUrl;

  // All portrait videos play at once, muted + loop (no pause when off-strip).
  React.useEffect(() => {
    if (measureOnly) return undefined;
    const v = videoRef.current;
    if (!v || !isVideo) return undefined;
    v.muted = true;
    v.loop = true;
    const play = () => {
      v.play().catch(() => {});
    };
    play();
    v.addEventListener("loadeddata", play);
    v.addEventListener("canplay", play);
    const onVisibility = () => {
      if (document.visibilityState === "visible") play();
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      v.removeEventListener("loadeddata", play);
      v.removeEventListener("canplay", play);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [measureOnly, isVideo, item.mediaUrl]);

  return (
    <article
      className="relative w-[min(72vw,200px)] shrink-0 snap-center overflow-hidden rounded-[1.5rem] bg-[#3e2723] shadow-[0_12px_40px_-12px_rgba(62,39,35,0.35)] sm:w-[220px] md:w-[240px] lg:w-[260px]"
    >
      <div className="relative aspect-[9/16] w-full">
        {measureOnly ? null : embed ? (
          <iframe
            src={embed}
            title={item.name || "Video"}
            className="absolute inset-0 h-full w-full border-0 object-cover"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          />
        ) : isVideo ? (
          <video
            ref={videoRef}
            src={item.mediaUrl}
            poster={item.posterUrl || undefined}
            muted
            loop
            playsInline
            autoPlay
            preload="auto"
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : blockedThirdParty ? (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-[#fbd9e5]/25 p-4 text-center">
            {item.posterUrl ? (
              <img src={item.posterUrl} alt={item.imageAlt?.trim() || item.name || ""} className="absolute inset-0 h-full w-full object-cover opacity-40" />
            ) : null}
            <Camera className="relative h-8 w-8 text-[#3e2723]/35" />
            <p className="relative text-[11px] leading-snug text-[#3e2723]/55">
              Activează cookie-urile Marketing pentru a reda video-ul.
            </p>
          </div>
        ) : item.mediaUrl ? (
          <img src={item.mediaUrl} alt={item.imageAlt?.trim() || item.name || ""} className="absolute inset-0 h-full w-full object-cover" />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center bg-[#fbd9e5]/30 text-[#3e2723]/40">
            <Camera className="h-10 w-10" />
          </div>
        )}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-[#3e2723]/90 via-[#3e2723]/40 to-transparent p-4 pt-12">
          {item.name ? (
            <p className="font-display text-sm font-bold text-[#fbd9e5]">{item.name}</p>
          ) : null}
          {item.role ? <p className="mt-0.5 text-[11px] text-[#fbd9e5]/75">{item.role}</p> : null}
        </div>
      </div>
    </article>
  );
}

function PortraitShowcase({ scrollSeconds }) {
  const { t } = useLanguage();
  const items = (t.portraitShowcase?.items || []).filter((x) => x?.mediaUrl?.trim());
  if (items.length === 0) return null;

  const cards = items.map((item, i) => (
    <PortraitMediaCard key={`${item.mediaUrl}-${i}`} item={item} />
  ));

  return (
    <div data-testid="portrait-showcase" className="min-w-0 max-w-full">
      <SectionHeading
        center
        eyebrow={t.portraitShowcase.eyebrow}
        title={t.portraitShowcase.title}
      />
      <div className="mt-8 min-w-0 max-w-full overflow-hidden">
        <ScrollStrip duration={scrollSeconds} gap="0.875rem">
          {cards}
        </ScrollStrip>
      </div>
    </div>
  );
}

function PartnerLogos({ scrollSeconds }) {
  const { t } = useLanguage();
  const items = (t.partners?.items || []).filter((x) => x?.logoUrl?.trim());
  if (items.length === 0) return null;

  const logos = items.map((p, i) => {
    const inner = (
      <span className="flex h-14 w-32 shrink-0 items-center justify-center rounded-xl border border-[#3e2723]/[0.06] bg-white px-3 sm:h-16 sm:w-40">
        <img
          src={p.logoUrl}
          alt={p.imageAlt?.trim() || p.name || "Partner"}
          className="max-h-[70%] max-w-[85%] object-contain opacity-60 grayscale transition-all duration-300 hover:opacity-100 hover:grayscale-0"
        />
      </span>
    );
    return p.href?.trim() ? (
      <a key={i} href={p.href} target="_blank" rel="noopener noreferrer" className="shrink-0 snap-center">
        {inner}
      </a>
    ) : (
      <span key={i} className="shrink-0 snap-center">
        {inner}
      </span>
    );
  });

  return (
    <div data-testid="partners-section" className="mt-14 min-w-0 max-w-full sm:mt-16">
      <SectionHeading center eyebrow={t.partners.eyebrow} title={t.partners.title} fit="oneLine" />
      <div className="mt-8 min-w-0 max-w-full overflow-hidden">
        <ScrollStrip duration={scrollSeconds} gap="1.25rem">
          {logos}
        </ScrollStrip>
      </div>
    </div>
  );
}

function MediaAndPartnersBlock() {
  const { homepageSettings } = useLanguage();
  const showPortrait = homepageSettings.showPortrait;
  const showPartners = homepageSettings.showPartners;
  if (!showPortrait && !showPartners) return null;

  return (
    <section
      className="relative overflow-x-clip bg-[#faf8f8] py-20 sm:py-28"
      data-testid="media-partners-block"
    >
      <div className="mx-auto min-w-0 max-w-7xl px-6 sm:px-12 lg:px-20">
        {showPortrait ? (
          <PortraitShowcase scrollSeconds={homepageSettings.portraitScrollSeconds} />
        ) : null}
        {showPartners ? (
          <PartnerLogos scrollSeconds={homepageSettings.partnersScrollSeconds} />
        ) : null}
      </div>
    </section>
  );
}

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: i * 0.08 },
  }),
};

function SectionHeading({ eyebrow, title, subtitle, dark = false, center = false, fit = "default" }) {
  const oneLine = fit === "oneLine";
  const eyebrowClass = `text-xs sm:text-sm font-bold uppercase tracking-[0.3em] ${
    dark ? "text-[#fbd9e5]/70" : "text-[#3e2723]/60"
  }`;
  const titleClass = oneLine
    ? `font-display font-black leading-[1.08] text-[clamp(1.35rem,4.2vw,3.15rem)] md:whitespace-nowrap ${
        dark ? "text-[#fbd9e5]" : "text-[#3e2723]"
      }`
    : `font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.02] ${
        dark ? "text-[#fbd9e5]" : "text-[#3e2723]"
      }`;
  const subtitleClass = `text-lg leading-relaxed ${dark ? "text-[#fbd9e5]/80" : "text-[#7a5c55]"}`;

  return (
    <div
      className={
        center
          ? `mx-auto text-center ${oneLine ? "max-w-5xl px-1" : "max-w-3xl"}`
          : oneLine
            ? "max-w-5xl"
            : "max-w-3xl"
      }
    >
      <motion.p
        variants={fadeUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-80px" }}
        className={eyebrowClass}
      >
        {eyebrow}
      </motion.p>
      <motion.h2
        variants={fadeUp}
        custom={1}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, margin: "-80px" }}
        className={`mt-4 ${titleClass}`}
      >
        {title}
      </motion.h2>
      {subtitle ? (
        <motion.p
          variants={fadeUp}
          custom={2}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className={`mt-5 ${subtitleClass}`}
        >
          {subtitle}
        </motion.p>
      ) : null}
    </div>
  );
}

function Hero() {
  const { t } = useLanguage();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      ref={ref}
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#3e2723]"
      data-testid="hero-section"
    >
      {/* Parallax background */}
      <motion.div style={{ scale, y }} className="absolute inset-0">
        <img
          src="https://images.unsplash.com/photo-1764346550950-6426166a8c8c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzJ8MHwxfHNlYXJjaHw0fHxhYnN0cmFjdCUyMGdlb21ldHJpYyUyMHBpbmslMjBwYXN0ZWwlMjBiYWNrZ3JvdW5kfGVufDB8fHx8MTc3Njc4OTY2Mnww&ixlib=rb-4.1.0&q=85"
          alt=""
          className="h-full w-full object-cover"
        />
      </motion.div>

      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#3e2723]/20 via-[#3e2723]/55 to-[#3e2723]/95" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#3e2723]/60 via-transparent to-transparent" />

      {/* Content */}
      <motion.div
        style={{ opacity }}
        className="relative z-10 mx-auto flex min-h-[100svh] max-w-7xl flex-col justify-end px-6 sm:px-12 lg:px-20 pt-32 pb-20 sm:pb-28"
      >
        <div className="max-w-5xl">
          <div className="overflow-hidden">
            <motion.h1
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1, delay: 1.15, ease: [0.16, 1, 0.3, 1] }}
              className="font-display text-5xl sm:text-7xl lg:text-[8.5rem] font-black tracking-tighter leading-[0.92] text-[#fbd9e5]"
            >
              {t.hero.title_line1}
            </motion.h1>
          </div>
          <div className="overflow-hidden">
            <motion.h1
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              transition={{ duration: 1, delay: 1.3, ease: [0.16, 1, 0.3, 1] }}
              className="font-display text-5xl sm:text-7xl lg:text-[8.5rem] font-black tracking-tighter leading-[0.92] text-[#fbd9e5] italic"
            >
              {t.hero.title_line2}
            </motion.h1>
          </div>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.55 }}
            className="mt-8 max-w-2xl text-base sm:text-lg leading-relaxed text-[#fbd9e5]/80"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1.7 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/contact"
              className="group inline-flex items-center gap-2 rounded-full bg-[#fbd9e5] px-7 py-4 text-[15px] font-semibold text-[#3e2723] transition-all duration-300 hover:bg-white hover:-translate-y-1 shadow-[0_10px_40px_rgba(251,217,229,0.25)]"
              data-testid="hero-cta-primary"
            >
              {t.hero.cta_primary}
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
            </Link>
            <Link
              to={ROUTES.services}
              className="group inline-flex items-center gap-2 rounded-full border border-[#fbd9e5]/30 bg-[#fbd9e5]/5 backdrop-blur-xl px-7 py-4 text-[15px] font-semibold text-[#fbd9e5] transition-all duration-300 hover:bg-[#fbd9e5]/15"
              data-testid="hero-cta-secondary"
            >
              {t.hero.cta_secondary}
              <ArrowUpRight className="h-4 w-4" />
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 2 }}
          className="mt-20 flex items-center gap-3 text-[#fbd9e5]/60"
        >
          <motion.div
            animate={{ y: [0, 6, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="flex h-9 w-9 items-center justify-center rounded-full border border-[#fbd9e5]/30"
          >
            <ArrowDown className="h-3.5 w-3.5" />
          </motion.div>
          <span className="text-[11px] uppercase tracking-[0.3em]">{t.hero.scroll}</span>
        </motion.div>
      </motion.div>
    </section>
  );
}

function HeroMarquee() {
  const { t } = useLanguage();
  return (
    <div className="relative overflow-hidden bg-[#3e2723] py-6 sm:py-8 border-t border-[#fbd9e5]/10" data-testid="marquee">
      <Marquee className="[--duration:40s] [--gap:4rem] py-0" repeat={4}>
        {t.marquee.map((item, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-16 font-display text-3xl sm:text-5xl font-black italic tracking-tight text-[#fbd9e5] whitespace-nowrap"
          >
            {item}
            <span className="text-[#fbd9e5]/30">◆</span>
          </span>
        ))}
      </Marquee>
    </div>
  );
}

const WHY_ICONS = [Sparkles, Clapperboard, ScanEye, TrendingUp];

function WhyUs() {
  const { t } = useLanguage();

  return (
    <section className="relative overflow-hidden bg-[#faf8f8] py-24 sm:py-32" data-testid="why-us-section">
      <div
        aria-hidden
        className="pointer-events-none absolute -left-[20%] top-1/4 h-[min(520px,55vh)] w-[min(520px,55vw)] rounded-full bg-[#fbd9e5]/35 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-[15%] bottom-0 h-[min(400px,45vh)] w-[min(400px,45vw)] rounded-full bg-[#3e2723]/[0.07] blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#3e2723]/10 to-transparent"
      />

      <div className="relative mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <motion.h2
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          className="mx-auto max-w-4xl text-center font-display text-3xl font-black tracking-tighter text-[#3e2723] sm:text-4xl lg:text-5xl"
        >
          {t.stats.subtitle}
        </motion.h2>

        <div className="mx-auto mt-12 grid max-w-6xl gap-4 sm:mt-14 sm:grid-cols-2 sm:gap-5 lg:grid-cols-4 lg:gap-5">
          {t.stats.items.map((item, i) => {
            const Icon = WHY_ICONS[i] || Sparkles;
            return (
              <motion.article
                key={i}
                variants={fadeUp}
                custom={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                className="group relative flex h-full flex-col overflow-hidden rounded-[1.75rem] border border-[#3e2723]/[0.08] bg-gradient-to-br from-white via-white to-[#faf8f8] p-6 shadow-[0_12px_40px_-20px_rgba(62,39,35,0.18)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(62,39,35,0.28)] sm:rounded-[2rem] sm:p-7"
                data-testid={`why-us-item-${i}`}
              >
                <div
                  aria-hidden
                  className="pointer-events-none absolute -right-8 -top-8 h-28 w-28 rounded-full bg-[#fbd9e5]/40 blur-2xl transition duration-500 group-hover:bg-[#fbd9e5]/55"
                />
                <div className="relative flex items-start justify-between gap-3">
                  <div className="inline-flex h-11 w-11 items-center justify-center rounded-2xl bg-[#3e2723] text-[#fbd9e5] shadow-lg shadow-[#3e2723]/20 ring-2 ring-[#fbd9e5]/30">
                    <Icon className="h-5 w-5" strokeWidth={1.5} aria-hidden />
                  </div>
                  <span className="font-display text-[11px] font-bold tabular-nums tracking-[0.2em] text-[#3e2723]/30">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
                <h3 className="relative mt-5 font-display text-lg font-bold tracking-tight text-[#3e2723] sm:text-xl">
                  {item.title}
                </h3>
                <p className="relative mt-2 text-sm leading-relaxed text-[#7a5c55] sm:text-[15px]">{item.desc}</p>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}

function ServiceTiltCard({ slug, label, desc, index }) {
  const icons = { "social-media-management": Camera, "ads": Megaphone, "website": Monitor, "consultanta": Compass };
  const Icon = icons[slug] || Camera;
  const handleMouseMove = (e) => {
    const card = e.currentTarget;
    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const px = (x / rect.width - 0.5) * 2;
    const py = (y / rect.height - 0.5) * 2;
    card.style.setProperty("--rx", `${-py * 6}deg`);
    card.style.setProperty("--ry", `${px * 8}deg`);
    card.style.setProperty("--mx", `${x}px`);
    card.style.setProperty("--my", `${y}px`);
  };
  const reset = (e) => {
    const card = e.currentTarget;
    card.style.setProperty("--rx", "0deg");
    card.style.setProperty("--ry", "0deg");
  };

  return (
    <motion.div
      variants={fadeUp}
      custom={index}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, margin: "-50px" }}
      style={{ perspective: "1000px" }}
      data-testid={`home-service-card-${slug}`}
    >
      <Link
        to={servicePath(slug)}
        onMouseMove={handleMouseMove}
        onMouseLeave={reset}
        className="group relative block overflow-hidden rounded-[2rem] sm:rounded-[2.25rem] bg-[#fbd9e5] p-7 sm:p-8 transition-transform duration-500 h-full will-change-transform"
        style={{
          transform: "rotateX(var(--rx, 0deg)) rotateY(var(--ry, 0deg)) translateZ(0)",
          transformStyle: "preserve-3d",
          transitionProperty: "transform, box-shadow",
        }}
      >
        {/* Shine glow that follows cursor */}
        <span
          aria-hidden
          className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
          style={{
            background:
              "radial-gradient(420px circle at var(--mx, 50%) var(--my, 50%), rgba(255,255,255,0.55), transparent 45%)",
          }}
        />
        <div aria-hidden className="pointer-events-none absolute -right-20 -top-20 h-52 w-52 rounded-full bg-white/50 blur-3xl opacity-60 group-hover:opacity-90 transition-opacity" />
        <div className="relative flex flex-col h-full" style={{ transform: "translateZ(30px)" }}>
          <div className="flex items-center justify-between">
            <span className="inline-flex h-11 w-11 items-center justify-center rounded-full bg-[#3e2723] text-[#fbd9e5]">
              <Icon className="h-5 w-5" />
            </span>
            <span className="inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/70 border border-[#3e2723]/10 text-[#3e2723] group-hover:bg-[#3e2723] group-hover:text-[#fbd9e5] group-hover:rotate-45 transition-all duration-300">
              <ArrowUpRight className="h-3.5 w-3.5" />
            </span>
          </div>
          <h3 className="mt-6 font-display text-2xl sm:text-[26px] font-bold tracking-tight text-[#3e2723] leading-tight">
            {label}
          </h3>
          <p className="mt-2 text-[14px] leading-relaxed text-[#3e2723]/70">{desc}</p>
        </div>
      </Link>
    </motion.div>
  );
}

function Services() {
  const { t, serviceCards } = useLanguage();
  const menu = serviceCards;
  return (
    <section id="services" className="relative bg-white py-24 sm:py-32" data-testid="services-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <SectionHeading eyebrow={t.services.eyebrow} title={t.services.title} />
          <Link
            to={ROUTES.services}
            className="hidden md:inline-flex items-center gap-2 rounded-full border border-[#3e2723]/15 px-5 py-3 text-[13px] font-semibold text-[#3e2723] hover:bg-[#3e2723] hover:text-[#fbd9e5] transition-colors"
            data-testid="services-cta"
          >
            {t.services.cta}
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {menu.map((s, i) => (
            <ServiceTiltCard key={s.slug} slug={s.slug} label={s.label} desc={s.desc} index={i} />
          ))}
        </div>

        <Link
          to={ROUTES.services}
          className="mt-10 md:hidden inline-flex items-center gap-2 rounded-full border border-[#3e2723]/15 px-5 py-3 text-[13px] font-semibold text-[#3e2723]"
          data-testid="services-cta-mobile"
        >
          {t.services.cta}
          <ArrowUpRight className="h-3.5 w-3.5" />
        </Link>
      </div>
    </section>
  );
}

function Process() {
  const { t } = useLanguage();
  const icons = [Compass, Target, Rocket, TrendingUp];
  const steps = (Array.isArray(t?.process?.steps) ? t.process.steps : [])
    .filter((s) => s && typeof s === "object")
    .slice(0, 4)
    .map((s, i) => ({
      n: typeof s.n === "string" && s.n.trim() ? s.n : String(i + 1).padStart(2, "0"),
      title: typeof s.title === "string" ? s.title : "",
      desc: typeof s.desc === "string" ? s.desc : "",
    }));

  return (
    <section className="relative overflow-hidden bg-[#faf8f8] py-24 sm:py-32" data-testid="process-section">
      <div
        aria-hidden
        className="pointer-events-none absolute -left-[20%] top-1/4 h-[min(520px,55vh)] w-[min(520px,55vw)] rounded-full bg-[#fbd9e5]/35 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -right-[15%] bottom-0 h-[min(400px,45vh)] w-[min(400px,45vw)] rounded-full bg-[#3e2723]/[0.07] blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#3e2723]/10 to-transparent"
      />

      <div className="relative mx-auto max-w-7xl px-6 sm:px-12 lg:px-20">
        <SectionHeading eyebrow={t.process.eyebrow} title={t.process.title} fit="oneLine" />

        <div className="relative mt-14 rounded-[2.25rem] border border-[#3e2723]/[0.08] bg-gradient-to-b from-white via-white to-[#faf8f8] p-5 shadow-[0_24px_80px_-32px_rgba(62,39,35,0.2),inset_0_1px_0_rgba(255,255,255,0.95)] sm:p-7 lg:mt-16 lg:p-10">
          <div
            aria-hidden
            className="pointer-events-none absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-[#fbd9e5]/80 to-transparent sm:inset-x-12"
          />

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-6 lg:grid-cols-4 lg:gap-6">
            {steps.map((step, i) => {
              const Icon = icons[i];
              return (
                <motion.div
                  key={i}
                  variants={fadeUp}
                  custom={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-50px" }}
                  className="group relative min-h-[260px] transition-transform duration-500 lg:min-h-[300px] lg:[&:nth-child(2)]:translate-y-8 lg:[&:nth-child(4)]:translate-y-8"
                  data-testid={`process-step-${i}`}
                >
                  <div className="relative h-full rounded-[1.85rem] bg-gradient-to-br from-[#fbd9e5]/90 via-white to-white p-[1px] shadow-[0_12px_40px_-18px_rgba(62,39,35,0.25)] transition-shadow duration-300 group-hover:shadow-[0_20px_50px_-20px_rgba(62,39,35,0.35)]">
                    <div className="relative flex h-full min-h-[inherit] flex-col overflow-hidden rounded-[calc(1.85rem-1px)] bg-white p-7 sm:p-8">
                      <div
                        aria-hidden
                        className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-[#3e2723] via-[#b76e79] to-[#fbd9e5] opacity-90"
                      />
                      <span
                        aria-hidden
                        className="pointer-events-none absolute -right-0.5 -top-1 bg-gradient-to-br from-[#fce4ec] via-[#fbd9e5] to-[#e8b4c4] bg-clip-text font-display text-[5rem] font-black leading-[0.85] text-transparent select-none sm:text-[5.5rem]"
                      >
                        {step.n}
                      </span>
                      <div className="relative mt-1 flex flex-1 flex-col">
                        <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-[#3e2723] text-[#fbd9e5] shadow-lg shadow-[#3e2723]/25 ring-2 ring-[#fbd9e5]/40">
                          <Icon className="h-6 w-6" strokeWidth={1.5} aria-hidden />
                        </div>
                        <h3 className="mt-8 font-display text-xl font-bold tracking-tight text-[#3e2723] sm:text-2xl">
                          {step.title}
                        </h3>
                        <p className="mt-3 flex-1 text-sm leading-relaxed text-[#7a5c55] sm:text-[15px]">{step.desc}</p>
                        <div className="mt-6 flex items-center gap-2">
                          <span className="h-px flex-1 bg-gradient-to-r from-[#3e2723]/15 to-transparent" aria-hidden />
                          <span className="font-display text-[10px] font-bold uppercase tracking-[0.28em] text-[#3e2723]/35">
                            {step.n}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

function TestimonialCard({ quote, name, username, role, country }) {
  return (
    <figure className="w-[240px] sm:w-[260px] shrink-0 rounded-[1.5rem] border border-[#3e2723]/10 bg-white p-5 shadow-[0_8px_24px_rgba(62,39,35,0.06)]">
      <Quote className="h-4 w-4 text-[#3e2723]/40" />
      <blockquote className="mt-3 text-[13px] leading-relaxed text-[#3e2723] line-clamp-5">
        "{quote}"
      </blockquote>
      <figcaption className="mt-4 flex items-center gap-3">
        <Avatar className="h-9 w-9 ring-2 ring-[#fbd9e5]">
          <AvatarFallback className="bg-[#fbd9e5] text-[#3e2723] font-display font-bold text-sm">
            {name.charAt(0)}
          </AvatarFallback>
        </Avatar>
        <div className="min-w-0">
          <div className="flex items-center gap-1 text-[12.5px] font-semibold text-[#3e2723] truncate">
            {name} <span className="text-[10px] font-normal">{country}</span>
          </div>
          <p className="text-[10.5px] text-[#7a5c55] truncate">{username} · {role}</p>
        </div>
      </figcaption>
    </figure>
  );
}

function Testimonials() {
  const { t } = useLanguage();
  const items = t.testimonials.items;
  return (
    <section className="relative bg-[#3e2723] py-24 sm:py-32 overflow-hidden" data-testid="testimonials-section">
      <div aria-hidden className="pointer-events-none absolute -top-40 -right-32 h-[500px] w-[500px] rounded-full bg-[#fbd9e5]/10 blur-3xl" />
      <div aria-hidden className="pointer-events-none absolute -bottom-40 -left-32 h-[420px] w-[420px] rounded-full bg-[#fbd9e5]/8 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-20">
        <SectionHeading eyebrow={t.testimonials.eyebrow} title={t.testimonials.title} dark />

        <div className="mt-14 relative w-full min-w-0 max-w-full overflow-hidden h-[480px] sm:h-[600px] rounded-[2rem] sm:rounded-[2.5rem] border border-[#fbd9e5]/10 bg-[#2a1a17]/40">
          <div className="absolute inset-0 flex items-center justify-center overflow-hidden [perspective:1200px]">
            <div
              className="flex max-w-full flex-row items-center justify-center gap-3 sm:gap-4"
              style={{
                transform:
                  "translateZ(-60px) rotateX(12deg) rotateY(-6deg) rotateZ(8deg)",
              }}
            >
              <Marquee vertical pauseOnHover repeat={3} className="[--duration:38s]">
                {items.map((r) => <TestimonialCard key={`a-${r.username}`} {...r} />)}
              </Marquee>
              <Marquee vertical pauseOnHover reverse repeat={3} className="[--duration:44s]">
                {items.map((r) => <TestimonialCard key={`b-${r.username}`} {...r} />)}
              </Marquee>
              <Marquee vertical pauseOnHover repeat={3} className="[--duration:40s] hidden md:flex">
                {items.map((r) => <TestimonialCard key={`c-${r.username}`} {...r} />)}
              </Marquee>
              <Marquee vertical pauseOnHover reverse repeat={3} className="[--duration:48s] hidden xl:flex">
                {items.map((r) => <TestimonialCard key={`d-${r.username}`} {...r} />)}
              </Marquee>
            </div>
          </div>

          {/* Gradient fades */}
          <div className="pointer-events-none absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-[#3e2723] to-transparent" />
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-28 bg-gradient-to-t from-[#3e2723] to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-[#3e2723] to-transparent" />
          <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-[#3e2723] to-transparent" />
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  const { t } = useLanguage();
  return (
    <LandscapeCta
      eyebrow={t.cta_section.eyebrow}
      title={t.cta_section.title}
      subtitle={t.cta_section.subtitle}
      buttonLabel={t.cta_section.button}
      testId="cta-section"
      buttonTestId="cta-section-button"
    />
  );
}

export default function HomePage() {
  const { homepageSettings } = useLanguage();

  return (
    <main data-testid="home-page" className="w-full min-w-0 overflow-x-clip">
      <Hero />
      <HeroMarquee />
      {homepageSettings.showWhyUs !== false ? <WhyUs /> : null}
      <Services />
      <Process />
      <MediaAndPartnersBlock />
      {homepageSettings.showTestimonials ? <Testimonials /> : null}
      <CTASection />
    </main>
  );
}
